"use client";

import { useEffect, useState } from "react";

type FollowUp = {
  id: string;
  type: string;
  status: string;
  scheduledAt: string;
  notes?: string;
  customer: { name: string; phone: string };
  lead?: { title: string };
};

const TYPE_ICONS: Record<string, string> = {
  CALL: "📞",
  EMAIL: "📧",
  MEETING: "🤝",
  TASK: "✅",
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  DONE: "bg-green-100 text-green-800",
  CANCELLED: "bg-gray-100 text-gray-800",
};

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [form, setForm] = useState({ customerId: "", type: "CALL", scheduledAt: "", notes: "" });
  const [filter, setFilter] = useState("upcoming");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter === "upcoming") params.set("upcoming", "true");
    else if (filter === "done") params.set("status", "DONE");
    else params.set("status", "PENDING");
    fetch(`/api/follow-ups?${params}`)
      .then((r) => r.json())
      .then((d) => { setFollowUps(d.followUps); setTotal(d.total); setLoading(false); });
  };

  useEffect(() => { load(); }, [filter]);

  useEffect(() => {
    fetch("/api/customers?limit=100").then((r) => r.json()).then((d) => setCustomers(d.customers));
  }, []);

  const createFollowUp = async () => {
    await fetch("/api/follow-ups", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setShowForm(false);
    setForm({ customerId: "", type: "CALL", scheduledAt: "", notes: "" });
    load();
  };

  const markDone = async (id: string) => {
    await fetch(`/api/follow-ups/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "DONE" }),
    });
    load();
  };

  const isOverdue = (scheduledAt: string) => new Date(scheduledAt) < new Date();

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Follow-ups</h2>
          <p className="text-gray-500 mt-0.5">{total} items</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
        >
          + Schedule Follow-up
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {[["upcoming", "Upcoming"], ["all", "All Pending"], ["done", "Completed"]].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === val ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Follow-ups grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : followUps.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No follow-ups found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {followUps.map((fu) => (
            <div
              key={fu.id}
              className={`bg-white rounded-xl p-4 shadow-sm border transition-colors ${
                fu.status === "PENDING" && isOverdue(fu.scheduledAt)
                  ? "border-red-200 bg-red-50"
                  : "border-gray-100"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{TYPE_ICONS[fu.type] || "📋"}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{fu.customer.name}</p>
                    <p className="text-xs text-gray-400">{fu.customer.phone}</p>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[fu.status]}`}>
                  {fu.status}
                </span>
              </div>

              {fu.lead && (
                <p className="text-xs text-indigo-600 mb-2 truncate">🎯 {fu.lead.title}</p>
              )}

              <div className="flex items-center justify-between mt-3">
                <div>
                  <p className={`text-xs font-medium ${isOverdue(fu.scheduledAt) && fu.status === "PENDING" ? "text-red-600" : "text-gray-500"}`}>
                    {isOverdue(fu.scheduledAt) && fu.status === "PENDING" ? "⚠️ " : ""}
                    {new Date(fu.scheduledAt).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                  {fu.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{fu.notes}</p>}
                </div>
                {fu.status === "PENDING" && (
                  <button
                    onClick={() => markDone(fu.id)}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                  >
                    Done ✓
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Schedule Follow-up</h3>
            <div className="space-y-3">
              <select
                value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select Customer *</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="CALL">📞 Call</option>
                <option value="EMAIL">📧 Email</option>
                <option value="MEETING">🤝 Meeting</option>
                <option value="TASK">✅ Task</option>
              </select>
              <input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
              <textarea
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={createFollowUp}
                disabled={!form.customerId || !form.scheduledAt}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                Schedule
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
